import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ChangeDetectionStrategy
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import Session from 'store-management';

import { TCustomer } from 'src/app/modules/admin/customers/customers.types';
import { useLogoutMutation } from 'src/app/store/api/cohrex/endpoints/auth';
import { CustomerSwitchService } from '../../services/customer-switch/customer-switch.service';
import { TUser } from '../../types';
import { ROUTES } from './menu';
import { TSideNav } from './sidenav.types';
import { STORE_MANAGEMENT_KEY } from '../../../constants';

@Component({
  selector: 'cohrex-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SidenavComponent implements OnInit {
  session!: Session;
  private _collapsed?: boolean = false;
  @Input()
  set collapsed(value: boolean | undefined) {
    this._collapsed = value;
  }
  get collapsed(): boolean | undefined {
    return this._collapsed;
  }

  @Output()
  hideSideNavOnMobile: EventEmitter<boolean> = new EventEmitter();

  private _sideNavHidden?: boolean = false;
  @Input()
  set sideNavHidden(value: boolean | undefined) {
    this._sideNavHidden = value;
  }
  get sideNavHidden(): boolean | undefined {
    return this._sideNavHidden;
  }

  menus!: TSideNav[];
  user!: TUser;
  activeCustomer!: TCustomer;
  customerSwitched?: boolean;
  parentRoute!: string;
  logoutMutation = useLogoutMutation();

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    private customerSwitchService: CustomerSwitchService
  ) {
    this.session = new Session();
    this.user = this.session.get('user');
    this.customerSwitchService
      .isCustomerSwitched()
      .subscribe((switched: boolean) => {
        this.customerSwitched = switched;
        this.activeCustomer =
          this.session[switched ? 'get' : 'forget']('activeCustomer');
      });
    this.activeCustomer = this.session.get('activeCustomer');
  }

  ngOnInit(): void {
    const userRoles = this.user?.roles?.map((role) => role.alias.toLowerCase());
    this.menus = ROUTES.filter((route) => {
      const userHasRole = userRoles?.some((userRole) =>
        route.roles.includes(userRole)
      );
      return userHasRole;
    });
    const element = document.querySelector('#hideSideNav') as HTMLDivElement;
    element?.click();
  }

  isParentMenuActive(menus: TSideNav[]): boolean {
    const menuLinks = menus.map((menu) => menu.path);
    const splitUrl = this.router.url.split('/');
    const childUrl = `/${splitUrl?.[1]}/${splitUrl?.[2]}`;

    return menuLinks.includes(this.router.url) || menuLinks.includes(childUrl);
  }

  isActivePath(path: string): boolean {
    return this.router.url.includes(path);
  }

  handleHideSideNavOnMobile(event: Event): void {
    this.sideNavHidden = true;
    this.hideSideNavOnMobile.emit(true);

    const target = event.target as HTMLDivElement;
    const nav = target.closest('aside');

    nav?.classList.remove('tw-w-[250px]');
    nav?.classList.add('tw-w-0');
  }

  handleParentMenuClick(event: Event) {
    const target = event.target as HTMLButtonElement;
    const button = target.closest('button');
    const childrenMenu = button?.nextElementSibling;
    const arrow = button?.querySelector('span');
    arrow?.classList.add('tw-rotate-180');
    if (childrenMenu?.classList.contains('tw-hidden')) {
      childrenMenu?.classList.remove(
        'tw-h-0',
        'tw-overflow-hidden',
        'tw-hidden'
      );
      childrenMenu?.classList.add(`tw-h-fit`, 'tw-overflow-auto', 'tw-visible');

      arrow?.classList.add('tw-rotate-180');
      return;
    }
    childrenMenu?.classList.remove(
      `tw-h-fit`,
      'tw-overflow-auto',
      'tw-visible'
    );
    childrenMenu?.classList.add('tw-h-0', 'tw-overflow-hidden', 'tw-hidden');

    arrow?.classList.remove('tw-rotate-180');
  }

  hideAllChildrenMenu(): void {
    const childrenMenu = document.querySelectorAll('.childrenMenu');
    childrenMenu.forEach((child) => {
      if (!child?.classList.contains('tw-hidden')) {
        child.classList.add('tw-hidden');
      }
    });
  }

  async handleLogout(): Promise<void> {
    this.logoutMutation({});
    localStorage.removeItem(STORE_MANAGEMENT_KEY);
    this.router.navigate(['/admin/auth/login']);
  }
}
